import { lazy, Suspense, useEffect } from "react";
import { ErrorBoundary } from "@/components/common/error-boundary";
import { Route, Switch, Redirect } from "wouter";
import { Toaster } from "sonner";
import Login from "@/pages/login";
import EsqueciSenha from "@/pages/esqueci-senha";
import ResetPassword from "@/pages/reset-password";
import AceitarConvite from "@/pages/aceitar-convite";
import SsoCallback from "@/pages/auth/login/sso";
import { RecaptchaWrapper } from "@/components/auth/recaptcha-wrapper";

// CAAS-4272: rotas pós-login viram lazy pra reduzir o bundle inicial
// (Resumo estava em ~2MB carregando TUDO de uma vez, levando 6s no LCP).
// Cada rota vira um chunk separado, carregado on-demand.
const DashboardPage = lazy(() => import("@/pages/dashboard"));
const TransacoesPage = lazy(() => import("@/pages/transacoes"));
const ClientesPage = lazy(() => import("@/pages/clientes"));
const ClientDetailPage = lazy(() => import("@/pages/clientes/client-detail"));
const EquipePage = lazy(() => import("@/pages/equipe"));
const SaquePage = lazy(() => import("@/pages/saque"));
const SolicitacoesPage = lazy(() => import("@/pages/solicitacoes"));
const EmbedLocsPage = lazy(() => import("@/pages/embed/locs"));
const EmbedRoadmapPage = lazy(() => import("@/pages/embed/roadmap"));
const SsoConfigPage = lazy(() => import("@/pages/configuracoes/sso"));
const ApiKeysPage = lazy(() => import("@/pages/configuracoes/api-keys"));
const TesourariaPage = lazy(() => import("@/pages/tesouraria"));
const SaldosPage = lazy(() => import("@/pages/saldos"));
const ConexoesPage = lazy(() => import("@/pages/configuracoes/conexoes"));
const CreateConnectionPage = lazy(() => import("@/pages/configuracoes/conexoes/criar"));
const SorsPage = lazy(() => import("@/pages/configuracoes/conexoes/sors"));
const ConnectionDetailPage = lazy(() => import("@/pages/configuracoes/conexoes/[id]"));
const ConnectionParamsPage = lazy(() => import("@/pages/configuracoes/conexoes/[id]/parametros"));
const NotificacoesPage = lazy(() => import("@/pages/notificacoes"));
const StatusPagePage = lazy(() => import("@/pages/status-page"));
// CAAS-4014 (liqi-as-exchange) — Conciliação: detalhe da divergência
const DivergenceDetailPage = lazy(() => import("@/pages/conciliacoes/divergencia"));
const DivergenciasListPage = lazy(() => import("@/pages/conciliacoes/divergencias"));
// CAAS-4014 (liqi-as-exchange) — Conciliação: Pendências + Saldo em contestação (handoff C)
const PendenciasPage = lazy(() => import("@/pages/conciliacoes/pendencias"));
const SaldoEmContestacaoPage = lazy(() => import("@/pages/conciliacoes/saldo-em-contestacao"));
// CAAS-4014 (liqi-as-exchange) — Conciliação: núcleo (Lista + Detalhe + Transações + Novo Corte) (handoff A)
const ConciliacoesListPage = lazy(() => import("@/pages/conciliacoes"));
const ConciliacoesTransacoesPage = lazy(() => import("@/pages/conciliacoes/transacoes"));
const ConciliacoesNovoCortePage = lazy(() => import("@/pages/conciliacoes/novo-corte"));
const ConciliacaoDetalhePage = lazy(() => import("@/pages/conciliacoes/detalhe"));

function RouteFallback() {
  return (
    <div
      className="flex items-center justify-center py-16"
      data-testid="route-suspense"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
import { useAuthStore } from "@/stores/auth.store";
import { useThemeStore } from "@/stores/theme.store";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import MainLayout from "@/components/layout/main-layout";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LeoPanel } from "@/components/leo/leo-panel";


function AuthenticatedApp() {
  return (
    <MainLayout>
      <Suspense fallback={<RouteFallback />}>
      <Switch>
        {/* Principal — Dashboard: ADMIN, MANAGER, ANALYST → ADMINISTRACAO, BACKOFFICE, VIEWER */}
        <Route path="/">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }, { role: "VIEWER" }]}>
            <DashboardPage />
          </ProtectedRoute>
        </Route>
        {/* Transações: ADMIN, MANAGER, SUPPORT → ADMINISTRACAO, BACKOFFICE */}
        <Route path="/transacoes">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <TransacoesPage />
          </ProtectedRoute>
        </Route>
        {/* Clientes: ADMIN, MANAGER, SUPPORT → ADMINISTRACAO, BACKOFFICE */}
        <Route path="/clientes/:profileName">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <ClientDetailPage />
          </ProtectedRoute>
        </Route>
        <Route path="/clientes">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <ClientesPage />
          </ProtectedRoute>
        </Route>

        {/* Operações — Solicitações: ADMIN, MANAGER → ADMINISTRACAO, BACKOFFICE (FULL_ACCESS).
            CAAS-4550: o par {role, permission} é obrigatório — sem `permission`,
            `checkPermission` casa qualquer permission daquela role e o perfil Suporte
            (BACKOFFICE/READ_ONLY) entraria junto. */}
        <Route path="/solicitacoes">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE", permission: "FULL_ACCESS" }]}>
            <SolicitacoesPage />
          </ProtectedRoute>
        </Route>
        {/* Saque: ADMIN, MANAGER → ADMINISTRACAO, BACKOFFICE (FULL_ACCESS) */}
        <Route path="/saque">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO", permission: "FULL_ACCESS" }, { role: "BACKOFFICE", permission: "FULL_ACCESS" }]}>
            <SaquePage />
          </ProtectedRoute>
        </Route>
        {/* Saldos: ADMIN, MANAGER → ADMINISTRACAO, BACKOFFICE */}
        <Route path="/saldos">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <SaldosPage />
          </ProtectedRoute>
        </Route>
        {/* Tesouraria: ADMIN, MANAGER → ADMINISTRACAO, BACKOFFICE (FULL_ACCESS) */}
        <Route path="/tesouraria">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO", permission: "FULL_ACCESS" }, { role: "BACKOFFICE", permission: "FULL_ACCESS" }]}>
            <TesourariaPage />
          </ProtectedRoute>
        </Route>

        {/* Embed — Roadmap (board iframe): qualquer role autenticado */}
        <Route path="/embed/roadmap">
          <ProtectedRoute>
            <EmbedRoadmapPage />
          </ProtectedRoute>
        </Route>
        {/* Embed — Chamados/LOCs (board iframe): qualquer role autenticado */}
        <Route path="/embed/locs">
          <ProtectedRoute>
            <EmbedLocsPage />
          </ProtectedRoute>
        </Route>
        {/* Status Page: todos os roles */}
        <Route path="/status-page" component={StatusPagePage} />

        {/* Configurações — SSO: ADMIN, SUPPORT → ADMINISTRACAO */}
        <Route path="/configuracoes/sso">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }]}>
            <SsoConfigPage />
          </ProtectedRoute>
        </Route>
        {/* API Keys: ADMIN, SUPPORT → ADMINISTRACAO */}
        <Route path="/configuracoes/api-keys">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }]}>
            <ApiKeysPage />
          </ProtectedRoute>
        </Route>
        {/* Conexões: ADMIN, MANAGER → ADMINISTRACAO, BACKOFFICE */}
        <Route path="/configuracoes/conexoes/criar">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <CreateConnectionPage />
          </ProtectedRoute>
        </Route>
        <Route path="/configuracoes/conexoes/sors">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <SorsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/configuracoes/conexoes/:id/parametros">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <ConnectionParamsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/configuracoes/conexoes/:id">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <ConnectionDetailPage />
          </ProtectedRoute>
        </Route>
        <Route path="/configuracoes/conexoes">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <ConexoesPage />
          </ProtectedRoute>
        </Route>

        {/* Notificações — sem equivalente no antigo, manter aberto */}
        <Route path="/notificacoes" component={NotificacoesPage} />

        {/* Administração — Equipe: ADMIN, MANAGER → ADMINISTRACAO, BACKOFFICE */}
        <Route path="/equipe">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <EquipePage />
          </ProtectedRoute>
        </Route>

        {/* Conciliação — Divergência (detalhe + fluxo): CAAS-4014 liqi-as-exchange.
            ADMINISTRACAO, BACKOFFICE (Itaú-facing, mesma faixa de saldos/tesouraria).
            APPEND — a rota base /conciliacoes + abas vem do handoff A; reconciliar no merge. */}
        <Route path="/conciliacoes/divergencia/:id">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <DivergenceDetailPage />
          </ProtectedRoute>
        </Route>
        {/* Lista de Divergências (CAAS-4014 handoff 2026-07-22). Literal, sem
            colisão com /conciliacoes/divergencia/:id (segmento distinto). */}
        <Route path="/conciliacoes/divergencias">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <DivergenciasListPage />
          </ProtectedRoute>
        </Route>

        {/* Conciliação — Pendências de Transferência + Saldo em contestação: CAAS-4014
            liqi-as-exchange (handoff C). ADMINISTRACAO, BACKOFFICE (Itaú-facing).
            APPEND — a rota base /conciliacoes + abas vem do handoff A; reconciliar no merge. */}
        <Route path="/conciliacoes/pendencias">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <PendenciasPage />
          </ProtectedRoute>
        </Route>
        <Route path="/conciliacoes/saldo-em-contestacao">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <SaldoEmContestacaoPage />
          </ProtectedRoute>
        </Route>

        {/* Conciliação — núcleo (Lista + Transações + Novo Corte + Detalhe): CAAS-4014
            liqi-as-exchange (handoff A). ADMINISTRACAO, BACKOFFICE (Itaú-facing).
            Rotas literais/prefixadas — sem :id no topo pra não colidir com as abas. */}
        <Route path="/conciliacoes/transacoes">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <ConciliacoesTransacoesPage />
          </ProtectedRoute>
        </Route>
        <Route path="/conciliacoes/novo-corte">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <ConciliacoesNovoCortePage />
          </ProtectedRoute>
        </Route>
        <Route path="/conciliacoes/detalhe/:id">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <ConciliacaoDetalhePage />
          </ProtectedRoute>
        </Route>
        <Route path="/conciliacoes">
          <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
            <ConciliacoesListPage />
          </ProtectedRoute>
        </Route>

        <Route>
          <Redirect to="/" />
        </Route>
      </Switch>
      </Suspense>
    </MainLayout>
  );
}

export default function App() {
  useEffect(() => {
    useThemeStore.getState().initialize();
    useAuthStore.getState().initialize();
  }, []);

  return (
    <TooltipProvider>
      <Toaster position="top-right" richColors closeButton />
      <Switch>
        <Route path="/login">
          <RecaptchaWrapper><Login /></RecaptchaWrapper>
        </Route>
        <Route path="/esqueci-senha">
          <RecaptchaWrapper><EsqueciSenha /></RecaptchaWrapper>
        </Route>
        <Route path="/reset-password">
          <RecaptchaWrapper><ResetPassword /></RecaptchaWrapper>
        </Route>
        <Route path="/aceitar-convite" component={AceitarConvite} />
        <Route path="/auth/login/sso" component={SsoCallback} />
        <Route>
          <ProtectedRoute>
            <ErrorBoundary>
              <AuthenticatedApp />
            </ErrorBoundary>
            <LeoPanel />
          </ProtectedRoute>
        </Route>
      </Switch>
    </TooltipProvider>
  );
}
